import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

/**
 * @typedef {{ amount: string, currencyCode: string }} MoneyV2
 * @typedef {{ amount: string }} MoneyV2Nullable
 * @typedef {{ url: string, altText: string | null }} Image
 * @typedef {{ id: string, title: string, handle: string, featuredImage: Image | null }} ApiProduct
 * @typedef {{ sku: string, barcode: string | null, taxable: boolean, price: MoneyV2, compareAtPrice: MoneyV2Nullable | null, product: ApiProduct }} ApiVariant
 */

class ProductCheaperAlternative extends HTMLElement {

  constructor() {
    super();
    this.searchForAlternative();
  }

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  /** @param {Event} event */
  onVariantUpdate = (event) => {

    const customEvent = /** @type {CustomEvent} */ (event);
    const detail = customEvent.detail;

    if (detail.data.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newElement = detail.data.html.querySelector('product-cheaper-alternative');
    if (!newElement) {
      this.style.display = 'none';
      this.innerHTML = '';
      return;
    }

    this.dataset.sku = newElement.dataset.sku;
    this.dataset.ean = newElement.dataset.ean;
    this.dataset.currentPrice = newElement.dataset.currentPrice;
    this.dataset.label = newElement.dataset.label;
    this.dataset.vendor = newElement.dataset.vendor;
    this.dataset.variantId = detail.resource?.id || null;

    this.style.display = 'none';
    this.innerHTML = '';
    this.searchForAlternative();

  };

  async searchForAlternative() {

    const sku = this.dataset.sku;
    const ean = this.dataset.ean;

    console.log('[CheaperAlternative] searching with SKU:', sku, 'EAN:', ean);

    if (!sku && !ean) {
      console.log('[CheaperAlternative] no SKU or EAN, skipping');
      return;
    }

    try {
      const alternative = await this.findCheaperAlternative(sku ?? '', ean ?? '');
      if (alternative) {
        this.render(alternative);
      } else {
        console.log('[CheaperAlternative] no matching alternative found');
      }
    } catch (err) {
      console.error('[CheaperAlternative] failed to search', err);
    }

  }

  /**
   * @param {string} sku
   * @param {string} ean
   * @returns {Promise<ApiVariant | null>}
   */
  async findCheaperAlternative(sku, ean) {

    const currentProductId = this.dataset.productId;
    const currentGid = `gid://shopify/Product/${currentProductId}`;

    const vendor = this.dataset.vendor;

    const queryStr = `(tag:Outlet OR tag:Begagnat)${vendor ? ` AND vendor:${JSON.stringify(vendor)}` : ''}`;

    console.log('[CheaperAlternative] API query string:', queryStr);
    console.log('[CheaperAlternative] current product GID:', currentGid);

    /** @type {{ id: string, title: string, handle: string, featuredImage: { url: string, altText: string | null } | null, variants: { edges: { node: ApiVariant }[] } }[]} */
    const products = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const afterClause = cursor ? `, after: ${JSON.stringify(cursor)}` : '';
      const graphqlQuery = `
        {
          products(first: 250, query: ${JSON.stringify(queryStr)}${afterClause}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                featuredImage {
                  url
                  altText
                }
                variants(first: 250) {
                  edges {
                    node {
                      sku
                      barcode
                      taxable
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch("https://buyn.se/api/2025-07/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "877da55bd1bfbfbb80858baf19e8b275"
        },
        body: JSON.stringify({ query: graphqlQuery })
      });

      const result = await response.json();
      console.log('[CheaperAlternative] page response:', result);

      const page = result.data?.products;
      if (!page) break;

      for (const edge of page.edges) {
        products.push(edge.node);
      }

      hasNextPage = page.pageInfo.hasNextPage;
      cursor = page.pageInfo.endCursor;
    }

    console.log('[CheaperAlternative] total products found:', products.length);

    // Flatten to variants, attaching product info, filtering to only those that match SKU or EAN
    /** @type {ApiVariant[]} */
    const variants = [];
    for (const product of products) {
      if (product.id === currentGid) continue;
      for (const edge of product.variants.edges) {
        const v = edge.node;
        if ((sku && v.sku === sku) || (ean && v.barcode === ean)) {
          variants.push({ ...v, product });
        }
      }
    }

    console.log('[CheaperAlternative] matching variants from other products:', variants.length, variants);

    const currentPrice = parseFloat(this.dataset.currentPrice ?? '0') / 100;

    const discounted = variants.filter((/** @type {ApiVariant} */ v) => {
      const isCheaper = parseFloat(v.price.amount) < currentPrice;
      console.log('[CheaperAlternative] variant', v.product.title, '| price:', v.price.amount, '| isCheaper:', isCheaper);
      return isCheaper;
    });

    console.log('[CheaperAlternative] discounted alternatives after filter:', discounted.length);

    if (!discounted.length) return null;

    discounted.sort((/** @type {ApiVariant} */ a, /** @type {ApiVariant} */ b) => parseFloat(a.price.amount) - parseFloat(b.price.amount));
    return discounted[0] ?? null;

  }

  /** @param {ApiVariant} variant */
  render(variant) {

    const product = variant.product;
    const imageUrl = product.featuredImage?.url;
    const imageAlt = product.featuredImage?.altText || product.title;
    const label = this.dataset.label || 'Billigare alternativ tillgängligt.';

    const currency = variant.price.currencyCode;
    const priceMinorUnits = convertMoneyToMinorUnits(variant.price.amount, currency) ?? 0;

    const taxable = variant.taxable;

    let compareAtHtml = '';
    if (variant.compareAtPrice) {
      const compareMinorUnits = convertMoneyToMinorUnits(variant.compareAtPrice.amount, currency) ?? 0;
      compareAtHtml = `<line-price class="compare-at-price" data-price="${compareMinorUnits}" data-taxable="${taxable}"></line-price>`;
    }

    const imageHtml = imageUrl
      ? `<img class="product-cheaper-alternative__image" src="${imageUrl}&width=120" alt="${imageAlt}" width="60" height="60" loading="eager">`
      : '';

    this.innerHTML = `
      <a href="/products/${product.handle}" class="product-cheaper-alternative__link">
        ${imageHtml}
        <div class="product-cheaper-alternative__text">
          <p class="product-cheaper-alternative__label">${label}</p>
          <p class="product-cheaper-alternative__title">${product.title}</p>
          <div class="product-cheaper-alternative__price">
            ${compareAtHtml}
            <line-price class="price" data-price="${priceMinorUnits}" data-taxable="${taxable}"></line-price>
          </div>
        </div>
      </a>
    `;

    const img = this.querySelector('img');
    if (img) {
      const show = () => { this.style.display = 'flex'; };
      if (img.complete) {
        show();
      } else {
        img.addEventListener('load', show, { once: true });
        img.addEventListener('error', show, { once: true });
      }
    } else {
      this.style.display = 'flex';
    }

  }

}

if (!customElements.get('product-cheaper-alternative')) {
  customElements.define('product-cheaper-alternative', ProductCheaperAlternative);
}
